import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl font-display font-semibold text-sm border border-border/70 cursor-pointer transition-all duration-200 ease-spring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground border-primary shadow-hard-sm hover:brightness-105 hover:saturate-150 hover:shadow-hard active:translate-y-px active:brightness-95 active:shadow-hard-sm',
        accent:
          'bg-riso-blue text-white border-riso-blue shadow-hard-blue hover:brightness-105 hover:saturate-125 hover:shadow-hard-blue-lg active:translate-y-px active:brightness-95 active:shadow-hard-blue',
        destructive:
          'bg-destructive text-destructive-foreground border-destructive shadow-hard-sm hover:brightness-105 hover:saturate-125 hover:shadow-hard active:translate-y-px active:brightness-95 active:shadow-hard-sm',
        outline:
          'bg-card text-foreground border-border shadow-hard-sm hover:border-riso-blue/35 hover:bg-muted/60 hover:shadow-hard active:translate-y-px active:shadow-hard-sm',
        secondary:
          'bg-secondary text-secondary-foreground border-border shadow-hard-sm hover:brightness-105 hover:saturate-125 hover:shadow-hard active:translate-y-px active:brightness-95 active:shadow-hard-sm',
        link: 'border-transparent bg-transparent shadow-none underline underline-offset-4 hover:text-foreground hover:decoration-2 hover:shadow-none hover:-translate-y-0 active:translate-y-0',
        'contained-pink':
          'bg-riso-pink text-white border-riso-pink shadow-hard-pink hover:brightness-105 hover:saturate-125 hover:shadow-hard-pink-lg active:translate-y-px active:brightness-95 active:shadow-hard-pink',
        'contained-teal':
          'bg-riso-teal text-white border-riso-teal shadow-hard-teal hover:brightness-105 hover:saturate-125 hover:shadow-hard-teal-lg active:translate-y-px active:brightness-95 active:shadow-hard-teal',
        'contained-amber':
          'bg-riso-amber text-white border-riso-amber shadow-hard-amber hover:brightness-105 hover:saturate-125 hover:shadow-hard-amber-lg active:translate-y-px active:brightness-95 active:shadow-hard-amber',
        'contained-blue':
          'bg-riso-blue text-white border-riso-blue shadow-hard-blue hover:brightness-105 hover:saturate-125 hover:shadow-hard-blue-lg active:translate-y-px active:brightness-95 active:shadow-hard-blue',
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm: 'h-9 px-3 text-xs',
        lg: 'h-12 px-8 text-base rounded-2xl',
        icon: 'h-10 w-10 rounded-full',
        'icon-sm': 'h-9 w-9 rounded-full',
      },
      hoverEffect: {
        pull: '',
        push: 'hover:translate-y-0.5',
        none: '',
      },
    },
    compoundVariants: [
      {
        hoverEffect: 'none',
        variant: [
          'default',
          'accent',
          'destructive',
          'outline',
          'secondary',
          'contained-pink',
          'contained-teal',
          'contained-amber',
          'contained-blue',
        ],
        className:
          'hover:!brightness-100 hover:!saturate-100 hover:!shadow-hard-sm active:!translate-y-0 active:!brightness-100 active:!shadow-hard-sm',
      },
      {
        hoverEffect: 'none',
        variant: 'accent',
        className: 'hover:!shadow-hard-blue active:!shadow-hard-blue',
      },
      {
        hoverEffect: 'none',
        variant: 'contained-pink',
        className: 'hover:!shadow-hard-pink active:!shadow-hard-pink',
      },
      {
        hoverEffect: 'none',
        variant: 'contained-teal',
        className: 'hover:!shadow-hard-teal active:!shadow-hard-teal',
      },
      {
        hoverEffect: 'none',
        variant: 'contained-amber',
        className: 'hover:!shadow-hard-amber active:!shadow-hard-amber',
      },
      {
        hoverEffect: 'none',
        variant: 'contained-blue',
        className: 'hover:!shadow-hard-blue active:!shadow-hard-blue',
      },
      {
        hoverEffect: 'none',
        variant: 'outline',
        className: 'hover:!border-border hover:!bg-card',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      hoverEffect: 'pull',
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, hoverEffect, asChild = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, hoverEffect, className }),
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
